import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import Keycloak from "keycloak-js";
import { App, PublicApp } from "./app/App";
import { NccAntdProvider } from "./app/NccAntdProvider";
import { isPublicPath, isOidcCallbackLocation } from "./app/publicRoute";
import { ActorContextProvider } from "./auth/ActorContext";
import { AuthProvider } from "./auth/AuthContext";
import { readOidcConfig } from "./auth/config";
import { assertOidcProviderAvailable } from "./auth/discovery";
import { bootstrapApplication } from "./auth/bootstrap";
import { KeycloakAuthenticationSession } from "./auth/KeycloakAuthenticationSession";
import { AuthenticationErrorPage } from "./auth/AuthenticationErrorPage";
import { installAuthenticationSession } from "./auth/session";
import "./styles/tokens.css";
import "./styles/app.css";

/**
 * Entrypoint có ĐÚNG hai nhánh, quyết định thuần bằng pathname (`app/publicRoute.ts`):
 *
 * 1. Route công khai — BA mẫu neo tuyệt đối: cổng `/public`, sơ đồ số
 *    `/public/cemeteries/:cemeterySlug/3d`, và hồ sơ công khai `/public/martyrs/<khoá>` (mã minh
 *    họa `demo-NNNN` VÀ mã liệt sĩ thật `LS-NNNNNN` của S12.4). Nhánh
 *    này render `PublicApp` trực tiếp và KHÔNG dựng Keycloak, không cài auth session, không gọi
 *    discovery/bootstrap. Nhờ vậy SCR-PUB-3D-01 chạy được khi Keycloak/OIDC không sẵn sàng.
 * 2. Mọi path khác — giữ nguyên hành vi xác thực hiện hành (Keycloak + session + bootstrap + AppShell).
 *
 * Matcher công khai là neo tuyệt đối nên không có path "gần giống" nào lọt vào nhánh 1.
 */

const root = document.getElementById("root");
if (!root) throw new Error("Root element #root was not found");

if (isPublicPath(window.location.pathname) && !isOidcCallbackLocation(window.location)) {
  // Nhánh công khai: chỉ AntD + router công khai, không chạm tầng xác thực.
  //
  // Cố ý KHÔNG bọc `StrictMode`: StrictMode (chỉ ở dev) mount → unmount → mount lại effect trên
  // cùng một instance. `CemeterySceneViewer` giữ `AbortController` trong ref tạo ở lượt render và
  // hủy nó ở cleanup unmount, nên lượt mount lại thừa hưởng đúng signal đã bị hủy và model không
  // bao giờ tải xong (spinner treo vĩnh viễn). Khung cảnh công khai phải tải được một lần và đúng
  // một lần — yêu cầu của e2e `s11-public-3d.spec.ts` (một lượt manifest cho mỗi lượt tải trang).
  // Nhánh quản trị bên dưới giữ nguyên `StrictMode` như hành vi hiện hành.
  createRoot(root).render(<PublicApp />);
} else {
  const config = readOidcConfig(import.meta.env, window.location);
  const session = new KeycloakAuthenticationSession(
    config,
    new Keycloak({
      url: config.url,
      realm: config.realm,
      clientId: config.clientId,
    }),
  );
  installAuthenticationSession(session);

  void bootstrapApplication({
    session,
    location: window.location,
    history: window.history,
    providerAvailable: () =>
      assertOidcProviderAvailable(config, new AbortController().signal),
    renderAuthenticated: () => {
      createRoot(root).render(
        <StrictMode>
          <NccAntdProvider>
            <AuthProvider session={session}>
              <ActorContextProvider>
                <App />
              </ActorContextProvider>
            </AuthProvider>
          </NccAntdProvider>
        </StrictMode>,
      );
    },
    renderFailure: (retry) => {
      createRoot(root).render(<AuthenticationErrorPage retry={retry} />);
    },
  });
}
